// client/src/components/stats/threebetMatchupBenchmarks.ts
//
// Benchmarks de 3Bet por matchup posicional (hero como 3bettor).
// Source: theoretical — derivados de consensus de coaching content
// público y solver outputs para situaciones canónicas.
//
// La key es `${heroPos}_vs_${openerPos}` — ej: "SB_vs_BTN" significa que
// hero está 3beteando desde SB sobre un open de BTN.
//
// IMPORTANTE: estos rangos varían MUCHO según:
// - Stack depth (a <25BB el 3bet es casi todo all-in, dinámica distinta)
// - Tipo de torneo (bounty hunter vs regular vs satellite)
// - Tendencias del villain pool
//
// Lectura general:
// - 3bet desde blinds (especialmente BB) vs late opens: ALTO (mucho fold equity)
// - 3bet vs opens tempranos (EP/HJ): BAJO (rangos tight, pocas hands valen)
// - 3bet desde IP (BTN vs CO): MEDIO (más sobre playability postflop)

import { MatchupKey } from '../../utils/pokerStats/types';
import { Benchmark } from './benchmarks';
import { StackKey } from './leakRanges';

const THEORETICAL_3BET_BENCHMARKS_DEEP: Partial<Record<MatchupKey, Benchmark>> = {
  // Hero BB vs * — máximo fold equity (ya invertiste el blind)
  'BB_vs_BTN': { min: 11, max: 16, source: 'theoretical' },
  'BB_vs_CO':  { min: 9,  max: 13, source: 'theoretical' },
  'BB_vs_HJ':  { min: 7,  max: 11, source: 'theoretical' },
  'BB_vs_EP':  { min: 5,  max: 9,  source: 'theoretical' },
  'BB_vs_SB':  { min: 14, max: 20, source: 'theoretical' },

  // Hero SB vs * — segundo más fold equity, pero peor postflop OOP
  'SB_vs_BTN': { min: 10, max: 15, source: 'theoretical' },
  'SB_vs_CO':  { min: 8,  max: 12, source: 'theoretical' },
  'SB_vs_HJ':  { min: 6,  max: 10, source: 'theoretical' },
  'SB_vs_EP':  { min: 4,  max: 8,  source: 'theoretical' },

  // Hero BTN vs * — IP, juego polarizado
  'BTN_vs_CO': { min: 8,  max: 12, source: 'theoretical' },
  'BTN_vs_HJ': { min: 7,  max: 11, source: 'theoretical' },
  'BTN_vs_EP': { min: 5,  max: 9,  source: 'theoretical' },

  // Hero CO vs * — todavía hay BTN para actuar atrás
  'CO_vs_HJ':  { min: 6,  max: 10, source: 'theoretical' },
  'CO_vs_EP':  { min: 4,  max: 8,  source: 'theoretical' },

  // Hero HJ vs EP — menos común, range tight
  'HJ_vs_EP':  { min: 4,  max: 8,  source: 'theoretical' },
};

// Short stack: a <25BB el 3bet es mayoritariamente shove o jam.
// Las frecuencias suben porque incorporás el merge range que busca fold equity.
const THEORETICAL_3BET_BENCHMARKS_SHORT: Partial<Record<MatchupKey, Benchmark>> = {
  // Desde blinds a <25BB, los 3bet shoves son muy frecuentes
  'BB_vs_BTN': { min: 14, max: 22, source: 'theoretical' },
  'BB_vs_CO':  { min: 12, max: 18, source: 'theoretical' },
  'BB_vs_HJ':  { min: 9,  max: 14, source: 'theoretical' },
  'BB_vs_EP':  { min: 6,  max: 11, source: 'theoretical' },
  'BB_vs_SB':  { min: 16, max: 24, source: 'theoretical' },

  'SB_vs_BTN': { min: 13, max: 20, source: 'theoretical' },
  'SB_vs_CO':  { min: 10, max: 16, source: 'theoretical' },
  'SB_vs_HJ':  { min: 8,  max: 13, source: 'theoretical' },
  'SB_vs_EP':  { min: 5,  max: 10, source: 'theoretical' },

  'BTN_vs_CO': { min: 9,  max: 14, source: 'theoretical' },
  'BTN_vs_HJ': { min: 8,  max: 13, source: 'theoretical' },
  'BTN_vs_EP': { min: 6,  max: 11, source: 'theoretical' },

  'CO_vs_HJ':  { min: 7,  max: 12, source: 'theoretical' },
  'CO_vs_EP':  { min: 5,  max: 10, source: 'theoretical' },

  'HJ_vs_EP':  { min: 5,  max: 10, source: 'theoretical' },
};

export const THREEBET_MATCHUP_BENCHMARKS: Record<
  StackKey,
  Partial<Record<MatchupKey, Benchmark>>
> = {
  short: THEORETICAL_3BET_BENCHMARKS_SHORT,
  mid:   THEORETICAL_3BET_BENCHMARKS_DEEP,
  deep:  THEORETICAL_3BET_BENCHMARKS_DEEP,
  all:   THEORETICAL_3BET_BENCHMARKS_DEEP,
};

export function getThreebetMatchupBenchmark(
  matchup: MatchupKey,
  stack: StackKey
): Benchmark | null {
  const stackBenchmarks = THREEBET_MATCHUP_BENCHMARKS[stack] || THREEBET_MATCHUP_BENCHMARKS.all;
  return stackBenchmarks[matchup] || null;
}
